import { useQuery } from "wasp/client/operations";
import {
  getAccountsByUser,
  getAccountGroupsByUser,
  getPropFirmTemplates,
  getTradingStrategiesByUser,
} from "wasp/client/operations";
import { Label } from "../../client/components/ui/label";
import { Button } from "../../client/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../client/components/ui/select";
import useTranslation from "../../client/hooks/useTranslation";
import type { ReportScope, ReportScopeType } from "../metrics/types";

type Props = {
  value: ReportScope;
  onChange: (next: ReportScope) => void;
};

export function ScopeSelector({ value, onChange }: Props) {
  const t = useTranslation();
  const { data: accountsData } = useQuery(getAccountsByUser);
  const accounts = (accountsData as any)?.accounts ?? [];
  const { data: groups } = useQuery(getAccountGroupsByUser);
  const { data: propfirms } = useQuery(getPropFirmTemplates);
  const { data: strategies } = useQuery(getTradingStrategiesByUser);

  const types: { value: ReportScopeType; label: string }[] = [
    { value: "all", label: t.reports.scopeAll },
    { value: "all-inactive", label: t.reports.scopeAllInactive },
    { value: "account", label: t.reports.scopeAccount },
    { value: "group", label: t.reports.scopeGroup },
    { value: "propfirm", label: t.reports.scopePropfirm },
    { value: "strategy", label: t.reports.scopeStrategy },
  ];

  function setType(nextType: ReportScopeType) {
    if (nextType === "all" || nextType === "all-inactive") {
      onChange({ type: nextType });
    } else {
      onChange({ type: nextType });
    }
  }

  function setId(id: string) {
    onChange({ type: value.type, id });
  }

  function renderSecondary() {
    switch (value.type) {
      case "account":
        return (
          <Select value={value.id ?? ""} onValueChange={setId}>
            <SelectTrigger>
              <SelectValue placeholder={t.reports.scopePickAccount} />
            </SelectTrigger>
            <SelectContent>
              {accounts.map((a: any) => (
                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case "group":
        return (
          <Select value={value.id ?? ""} onValueChange={setId}>
            <SelectTrigger>
              <SelectValue placeholder={t.reports.scopePickGroup} />
            </SelectTrigger>
            <SelectContent>
              {(groups ?? []).map((g: any) => (
                <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case "propfirm":
        return (
          <Select value={value.id ?? ""} onValueChange={setId}>
            <SelectTrigger>
              <SelectValue placeholder={t.reports.scopePickPropfirm} />
            </SelectTrigger>
            <SelectContent>
              {(propfirms ?? []).map((p: any) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case "strategy":
        return (
          <Select value={value.id ?? ""} onValueChange={setId}>
            <SelectTrigger>
              <SelectValue placeholder={t.reports.scopePickStrategy} />
            </SelectTrigger>
            <SelectContent>
              {(strategies ?? []).map((s: any) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case "all":
      default:
        return null;
    }
  }

  return (
    <div className="space-y-3">
      <Label>{t.reports.scopeLabel}</Label>
      <div className="flex flex-wrap gap-2">
        {types.map((tt) => (
          <Button
            key={tt.value}
            type="button"
            variant={value.type === tt.value ? "default" : "outline"}
            size="sm"
            onClick={() => setType(tt.value)}
          >
            {tt.label}
          </Button>
        ))}
      </div>
      {value.type !== "all" && value.type !== "all-inactive" && <div>{renderSecondary()}</div>}
    </div>
  );
}
